import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Landmark, Facebook, Send } from "lucide-react";
import { CONTACT, LOCATIONS } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YEARS = Array.from({ length: 12 }, (_, i) => String(2022 - i));

const initial = { child_name: "", birth_year: "", location: "", parent_phone: "", parent_email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/signups`, form);
      toast.success("Zgłoszenie wysłane! Skontaktujemy się w ciągu 24 godzin.");
      setForm(initial);
    } catch (err) {
      toast.error("Nie udało się wysłać zgłoszenia. Zadzwoń do nas: +48 500 111 775.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border-2 border-[#0B1120] bg-white px-4 py-3 text-sm font-medium text-[#0B1120] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FACC15]";

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 lg:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="font-accent uppercase tracking-[0.3em] text-blue-700 text-xs font-bold">06 — Kontakt</span>
          <h2 className="mt-4 font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl text-[#0B1120]">
            Zapisz dziecko na <span className="text-blue-700">trening próbny</span>
          </h2>
          <p className="mt-6 text-slate-600 text-base font-medium leading-relaxed">
            Pierwszy trening jest bezpłatny. Wypełnij formularz lub zadzwoń —
            pomożemy dobrać grupę i lokalizację.
          </p>

          <div className="mt-10 space-y-5">
            <div data-testid="contact-name" className="flex items-start gap-4">
              <MapPin className="text-blue-700 shrink-0 mt-1" size={20} />
              <div>
                <p className="font-display font-black uppercase tracking-tight text-[#0B1120]">{CONTACT.name}</p>
                <p className="text-sm text-slate-600 font-medium">{CONTACT.address}</p>
                <p className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">NIP: {CONTACT.nip}</p>
              </div>
            </div>
            <a data-testid="contact-phone-link" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group">
              <Phone className="text-blue-700 shrink-0" size={20} />
              <span className="font-accent font-bold text-[#0B1120] group-hover:text-blue-700 transition-colors">{CONTACT.phone}</span>
            </a>
            <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 group">
              <Mail className="text-blue-700 shrink-0" size={20} />
              <span className="font-accent font-bold text-[#0B1120] group-hover:text-blue-700 transition-colors">{CONTACT.email}</span>
            </a>
            <div data-testid="contact-bank" className="flex items-start gap-4">
              <Landmark className="text-blue-700 shrink-0 mt-1" size={20} />
              <div>
                <p className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500">Konto bankowe (składki do 10. dnia miesiąca)</p>
                <p className="font-accent font-bold text-[#0B1120] mt-1">{CONTACT.bank}</p>
              </div>
            </div>
            <a
              data-testid="contact-facebook-link"
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-blue-700 text-white font-display font-black uppercase tracking-tight px-6 py-3 border-2 border-blue-700 hover:bg-transparent hover:text-blue-700 transition-colors duration-200"
            >
              <Facebook size={18} /> Śledź nas na Facebooku
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="signup-form"
            className="bg-white border-2 border-[#0B1120] shadow-[8px_8px_0_#1D4ED8] p-8 lg:p-10"
          >
            <h3 className="font-display font-black uppercase tracking-tight text-2xl text-[#0B1120]">
              Formularz zapisu
            </h3>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Imię i nazwisko dziecka *</label>
                <input
                  data-testid="signup-child-name-input"
                  required
                  value={form.child_name}
                  onChange={set("child_name")}
                  placeholder="np. Jan Kowalski"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Rocznik *</label>
                <select data-testid="signup-birth-year-select" required value={form.birth_year} onChange={set("birth_year")} className={inputCls}>
                  <option value="">Wybierz rocznik</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Lokalizacja *</label>
                <select data-testid="signup-location-select" required value={form.location} onChange={set("location")} className={inputCls}>
                  <option value="">Wybierz lokalizację</option>
                  {LOCATIONS.map((l) => (
                    <option key={l.id} value={l.name}>{l.name} — {l.address}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Telefon rodzica *</label>
                <input
                  data-testid="signup-phone-input"
                  required
                  type="tel"
                  value={form.parent_phone}
                  onChange={set("parent_phone")}
                  placeholder="+48 ..."
                  className={inputCls}
                />
              </div>
              <div>
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">E-mail rodzica *</label>
                <input
                  data-testid="signup-email-input"
                  required
                  type="email"
                  value={form.parent_email}
                  onChange={set("parent_email")}
                  placeholder="np. jan@poczta.pl"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="font-accent text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Uwagi (opcjonalnie)</label>
                <textarea
                  data-testid="signup-message-textarea"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Doświadczenie piłkarskie, pytania..."
                  className={inputCls}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="signup-submit-button"
              className="mt-8 inline-flex items-center gap-3 bg-[#FACC15] text-[#0B1120] font-display font-black uppercase tracking-tight px-8 py-4 border-2 border-[#0B1120] shadow-[4px_4px_0_#0B1120] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_#0B1120] transition-transform duration-150 disabled:opacity-60 disabled:pointer-events-none"
            >
              <Send size={18} /> {sending ? "Wysyłanie..." : "Wyślij zgłoszenie"}
            </button>
            <p className="mt-4 text-xs text-slate-500 font-medium">
              Wysyłając formularz wyrażasz zgodę na kontakt w sprawie zapisu.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
